import { memo } from 'react'
import { Link } from 'react-router'
import {
  Search,
  Plus,
  Eye,
  ChevronLeft,
  ChevronRight,
  Archive,
  Users,
  Trash2,
  ArrowUpDown,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TableSkeleton } from '@/components/loading'
import { EmptyState } from '@/components/shared'
import { QueryError } from '@/components/error'
import type { Staff } from '@/types'
import { useStaffPage } from './StaffPage.logic'
import { statusBadgeVariant } from './StaffPage.static'

export function StaffPage() {
  const {
    t,
    staff,
    total,
    page,
    totalPages,
    inputValue,
    showArchive,
    sortOrder,
    isLoading,
    isError,
    isEmpty,
    deleteTargetId,
    refetch,
    setInputValue,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,
    setPage,
    handleSetSortOrder,
    handleToggleArchive,
    setDeleteTargetId,
    handleDeleteStaff,
  } = useStaffPage()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">
            {showArchive ? t('archiveDescription') : t('description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={showArchive ? 'default' : 'outline'} onClick={handleToggleArchive}>
            <Archive className="mr-2 size-4" />
            {t('archive')}
          </Button>
          <Button asChild>
            <Link to="/staff/new">
              <Plus className="mr-2 size-4" />
              {t('addStaff')}
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative min-w-0 flex-1 basis-full sm:basis-auto sm:max-w-sm">
          <button
            onClick={handleSearch}
            className="text-muted-foreground hover:text-foreground absolute left-3 top-1/2 -translate-y-1/2"
          >
            <Search className="size-4" />
          </button>
          <Input
            placeholder={t('searchPlaceholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-9 pr-8"
          />
          {inputValue && (
            <button
              onClick={handleClearSearch}
              className="text-muted-foreground hover:text-foreground absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <Select
          value={sortOrder}
          onValueChange={(val) => handleSetSortOrder(val === 'none' ? '' : (val as 'asc' | 'desc'))}
        >
          <SelectTrigger className="w-48">
            <ArrowUpDown className="mr-2 size-4 shrink-0" />
            <SelectValue placeholder={t('sort.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('sort.default')}</SelectItem>
            <SelectItem value="asc">{t('sort.nameAsc')}</SelectItem>
            <SelectItem value="desc">{t('sort.nameDesc')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <TableSkeleton rows={10} columns={6} />
      ) : isError ? (
        <QueryError onRetry={refetch} />
      ) : isEmpty ? (
        <EmptyState
          icon={Users}
          title={t('noStaff')}
          description={t('noStaffDescription')}
          action={{ label: t('addStaff'), to: '/staff/new' }}
        />
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('columns.name')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('columns.position')}</TableHead>
                  <TableHead>{t('columns.status')}</TableHead>
                  <TableHead className="hidden md:table-cell">
                    {t('columns.employmentType')}
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">{t('columns.phone')}</TableHead>
                  <TableHead className="w-[80px]">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((member) => (
                  <StaffRow key={member.id} member={member} t={t} onDelete={setDeleteTargetId} />
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
              <p className="text-muted-foreground text-sm">
                {t('pagination.showing', { count: total, page, totalPages })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="mr-1 size-4" />
                  {t('pagination.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  {t('pagination.next')}
                  <ChevronRight className="ml-1 size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <AlertDialog open={!!deleteTargetId} onOpenChange={() => setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteStaff')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirmDeleteStaff')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStaff}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const StaffRow = memo(function StaffRow({
  member,
  t,
  onDelete,
}: {
  member: Staff
  t: ReturnType<typeof useStaffPage>['t']
  onDelete: (id: string) => void
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {member.firstName} {member.lastName}
      </TableCell>
      <TableCell className="hidden md:table-cell">{member.position}</TableCell>
      <TableCell>
        <Badge variant={statusBadgeVariant[member.status]}>
          {t(`status.${member.status.toLowerCase()}` as 'status.active')}
        </Badge>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {t(`employmentTypes.${member.employmentType.toLowerCase()}` as 'employmentTypes.full_time')}
      </TableCell>
      <TableCell className="hidden lg:table-cell">{member.phone || '—'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/staff/${member.id}`}>
              <Eye className="mr-1 size-4" />
              {t('viewStaff')}
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-destructive hover:text-destructive"
            onClick={() => onDelete(member.id)}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
})
